import { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { mapAuthError } from '@mybusinessmate/api-client';
import { authClient } from './api';

/** The base32 secret inside an otpauth:// URI, for people who cannot scan the QR code. */
export function secretFromUri(uri: string): string {
  try {
    return new URL(uri).searchParams.get('secret') ?? '';
  } catch {
    return '';
  }
}

/**
 * The logic of turning on two-step with an authenticator app, shared by the client Security page and the staff
 * Security page (they look different, per their own Figma frames, but enrolling is the same):
 *   start(password) -> Better Auth `twoFactor.enable` -> QR code + secret + backup codes
 *   verify(code)    -> Better Auth `twoFactor.verifyTotp` -> two-step is on
 * Each returns a message to show when it fails, or `undefined` when it worked.
 */
export function useTwoFactorSetup() {
  const [totpUri, setTotpUri] = useState('');
  const [qr, setQr] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  useEffect(() => {
    if (!totpUri) {
      setQr('');
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(totpUri, { margin: 1, width: 192 }).then((url) => {
      if (!cancelled) setQr(url);
    });
    return () => {
      cancelled = true;
    };
  }, [totpUri]);

  const start = useCallback(async (password: string): Promise<string | undefined> => {
    const { data, error } = await authClient.twoFactor.enable({ password });
    const mapped = mapAuthError(error);
    if (mapped || !data || data.method !== 'totp') {
      return mapped?.code === 'INVALID_PASSWORD'
        ? 'That isn’t your password.'
        : (mapped?.message ?? 'Something went wrong.');
    }
    setTotpUri(data.totpURI);
    setBackupCodes(data.backupCodes);
    return undefined;
  }, []);

  const verify = useCallback(async (code: string): Promise<string | undefined> => {
    const { error } = await authClient.twoFactor.verifyTotp({ code });
    const mapped = mapAuthError(error);
    if (!mapped) return undefined;
    return mapped.kind === 'invalid_code' ? 'That code didn’t work. Check it and try again.' : mapped.message;
  }, []);

  const reset = useCallback(() => {
    setTotpUri('');
    setBackupCodes([]);
  }, []);

  return { totpUri, secret: secretFromUri(totpUri), qr, backupCodes, start, verify, reset };
}
